/* eslint-disable camelcase */
import { reactive, computed } from 'vue';

import useMainState from '@/state/useMainState';
import useDebugState from '@/state/useDebugState';
import isSoftBrandUser from '@/utils/isSoftBrandUser';

const { isDestinationUser } = useDebugState();
const { userProperties } = useMainState();

const userData = reactive({
  firstName: '',
  lastName: '',
  accountKey: '',
  premiumLevel: 1,
  language: 'en',
  email: '',
  currency: '',
  id: undefined,
  legacyId: '',
  access: 1,
  personalizationAccess: undefined,
  overviewPermissions: undefined,
  destinationCountdown: 0,
  accountId: '',
  hasAcceptedTerms: false,
  ernest: false,
});
const isKeyUser = () => userData.access === 'key';
const isViewUser = () => userData.access === 'view';
const isDMOUser = computed(() => (userData.permissions?.destination === 2
  || isDestinationUser.value));
const isHybridUser = computed(() => !!(isDMOUser.value && userProperties.value.length));
const isStrictlyDMOUser = computed(() => isDMOUser.value && !userProperties.value.length);

const isSoftBrandUserFlag = computed(() => isSoftBrandUser(userData.email));

/** Replaces LaunchDarkly `upselling` (bd-only users without Personalization, not destination-only). */
const showUpselling = computed(() => userData.personalizationAccess === false
  && userData.permissions?.destination !== 2);

const setUserData = (key, value) => {
  if (key && typeof value !== 'undefined') {
    userData[key] = value;
  }
};

const setUser = (user) => {
  const {
    firstName,
    lastName,
    language,
    email,
    currency,
    id,
    legacyId,
    access,
    personalizationAccess,
    hasAcceptedTerms,
    overviewPermissions,
    permissions,
    destinationCountdown,
    demand,
    safe_direct,
    predictive_personalization,
    predictive_audience,
    ernest,
    accountId,
    type,
    role,
  } = user;

  setUserData('language', language);
  setUserData('firstName', firstName);
  setUserData('lastName', lastName);
  setUserData('email', email);
  setUserData('currency', currency);
  setUserData('id', id);
  setUserData('legacyId', legacyId);
  setUserData('access', access);
  setUserData('personalizationAccess', personalizationAccess);
  setUserData('hasAcceptedTerms', hasAcceptedTerms);
  setUserData('overviewPermissions', overviewPermissions);
  setUserData('permissions', permissions);
  setUserData('destinationCountdown', destinationCountdown);
  setUserData('demand', demand);
  setUserData('safe_direct', safe_direct);
  setUserData('predictive_personalization', predictive_personalization);
  setUserData('predictive_audience', predictive_audience);
  setUserData('ernest', ernest);
  setUserData('accountId', accountId);
  setUserData('type', type);
  setUserData('role', role);
};

const isDemoUser = computed(() => [
  1034283, // productdemo@thehotelsnetwork.com
  1038073, // benchdirect_demo@thehotelsnetwork.com
  1041305, // IQS_benchdirect@thehotelsnetwork.com
  1037485, // tinybirdsdemo@thehotelsnetwork.com
  // multilanguage demo users
  1039331,
  1039332,
  1039333,
  1039334,
  1039335,
].indexOf(userData.legacyId) !== -1);

export default function useUserState() {
  return {
    userData,
    isDMOUser,
    isHybridUser,
    isStrictlyDMOUser,
    isSoftBrandUser: isSoftBrandUserFlag,
    showUpselling,
    setUser,
    setUserData,
    isKeyUser,
    isViewUser,
    isDemoUser,
  };
}
